/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Container 클래스를 정의한다.
 *
 * @file /src/components/form/field/container/Container.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 30.
 */
import * as parent from '@components/form/field/Field';
import { Component } from '@base/Component';
import { FieldDefaults } from '@components/form/Form';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import './Container.css';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 정렬방향
     */
    direction?: 'row' | 'column';

    /**
     * 에러메시지 통합여부
     */
    combineValidate?: boolean;

    /**
     * 필드 기본값
     */
    fieldDefaults?: FieldDefaults;

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class Container extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'container';

    /**
     * 에러메시지 통합여부
     */
    combineValidate: boolean;

    /**
     * 필드 기본값
     */
    fieldDefaults: FieldDefaults;

    /**
     * 필드 Dom
     */
    $fields: Dom = null;

    /**
     * 필드 컨테이너 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Omit<Properties<Container>, 'value'> = null) {
        super(properties);

        this.layout = this.properties?.direction == 'row' ? 'column' : 'row';

        if ((this.properties?.combineValidate ?? null) === null) {
            this.combineValidate = this.label !== null && this.properties.direction == 'row';
        } else {
            this.combineValidate = this.properties.combineValidate === true;
        }

        this.fieldDefaults = this.properties?.fieldDefaults ?? null;
    }

    /**
     * 자식 컴포넌트를 초기화한다.
     */
    initItems(): void {
        super.initItems();
        this.initAllowBlank();
    }

    /**
     * 하위 필드를 조사하여 빈값허용여부를 초기화한다.
     */
    initAllowBlank(): void {
        this.allowBlank = true;
        for (const item of this.items ?? []) {
            if (item instanceof parent.Field) {
                this.allowBlank = this.allowBlank == true && item.allowBlank;
                this.validations.set(item, item.getValidation());
            }
        }

        if (this.allowBlank === false) {
            this.$getContainer().addClass('required');
        }
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<object> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        if (this.inputName !== null) {
            const values: { [key: string]: any } = {};
            for (const field of this.items) {
                if (field instanceof parent.Field) {
                    Object.assign(values, await field.getValues());
                }
            }

            return values;
        } else {
            return null;
        }
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: { [key: string]: any }, is_init: boolean = false): Promise<void> {
        await this.lockValue();

        if (this.inputName !== null) {
            for (const name in value) {
                await this.getField(name)?.setValue(value[name]);
            }

            await this.releaseValue(value, is_init);
        }
    }

    /**
     * 자식 필드를 담을 DOM 을 가져온다.
     *
     * @returns 자식필드 DOM
     */
    $getFields(): Dom {
        if (this.$fields === null) {
            this.$fields = Html.create('div', { 'data-role': 'fields' });
        }

        return this.$fields;
    }

    /**
     * 필드값이 유효한지 확인한다.
     * 자식필드로 부터 유효성 결과를 받으므로, 함수를 재정의한다.
     *
     * @returns 필드값 유효성결과
     */
    protected async validate(): Promise<{ valid: boolean; message?: string }> {
        return { valid: true };
    }

    /**
     * 필드 유효성 검증결과 통합여부를 가져온다.
     *
     * @returns 필드 유효성 검증결과 통합여부
     */
    isCombineValidate(): boolean {
        return this.combineValidate;
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        for (const item of this.items) {
            item.setDisabled(disabled);
        }

        return super.setDisabled(disabled);
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        if (this.fieldDefaults !== null) {
            const labelWidth = this.fieldDefaults.labelWidth ?? null;
            const labelLength = this.fieldDefaults.labelLength ?? null;
            if (labelWidth !== null) {
                this.$getComponent().setStyleProperty('--aui-form-label-width', `${labelWidth}px`);
            } else if (labelLength !== null) {
                this.$getComponent().setStyleProperty('--aui-form-label-width', `${labelLength}rem`);
            }

            const labelPosition = this.fieldDefaults.labelPosition ?? 'left';
            if (labelPosition != 'left') {
                this.$getComponent().setStyleProperty('--aui-form-label-width', '100%');
                this.$getComponent().setStyleProperty('--aui-form-columns', '1fr auto');
                this.$getComponent().setStyleProperty(
                    '--aui-form-areas',
                    `'label label' 'content buttons' 'helptext helptext'`
                );
            }

            const labelAlign = this.fieldDefaults.labelAlign ?? (labelPosition == 'top' ? 'left' : 'right');
            if (labelAlign != 'right') {
                this.$getComponent().setStyleProperty('--aui-form-label-align', labelAlign);
                this.$getComponent().setStyleProperty('--aui-form-label-padding', '0');
            }

            const labelSeparator = this.fieldDefaults.labelSeparator ?? (labelPosition == 'top' ? '' : ':');
            if (labelSeparator !== ':') {
                this.$getComponent().setStyleProperty('--aui-form-label-separator', `"${labelSeparator}"`);
            }
        }

        await super.renderDom();
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        /**
         * 콘텐츠 DOM 에 자식 컴포넌트의 DOM 을 추가하고, 자식 컴포넌트를 렌더링한다.
         */
        const $children = document.createDocumentFragment();
        for (const item of this.items ?? []) {
            $children.append(item.$getComponent().getEl());
        }

        this.$getContent().append($children);
        await Promise.all((this.items ?? []).map((child) => child.render()));
    }

    /**
     * 자식 컴포넌트가 추가되었을 때 이벤트를 처리한다.
     *
     * @param item - 추가된 자식 컴포넌트
     */
    async onAppendItem(item: Component): Promise<void> {
        await super.onAppendItem(item);

        if (item instanceof parent.Field) {
            this.initAllowBlank();
        }
    }

    /**
     * 자식 컴포넌트가 제거되었을 때 이벤트를 처리한다.
     *
     * @param item - 제거된 자식 컴포넌트
     */
    async onRemoveItem(item: Component): Promise<void> {
        await super.onRemoveItem(item);

        if (item instanceof parent.Field) {
            this.initAllowBlank();
        }
    }
}
